import { useAuth } from '@/store/useAuth';
import { useUpdateUserData } from '@/hooks/use-update-profile';
import { generatePath, useNavigate } from 'react-router';
import { routesApp } from '@/constans/routes';
import React, { useCallback, useEffect } from 'react';
import { UploadPhotoRegistartionForm } from './components/upload-photo-registartion-form';
import { NameRegistartionForm } from './components/name-registartion-form';
import { DateRegistartionForm } from './components/date-registartion-form';
import {
    SettingInfoRegistrationForm
} from '@/components/form-registration-v2/components/setting-info-registartion-form';
import { TargetInfoRegistrationForm } from '@/components/form-registration-v2/components/target-info-registartion-form';
import {
    DirectionInfoRegistrationForm
} from '@/components/form-registration-v2/components/direction-info-registartion-form';
import { AboutAndTravelPlansRegistartionForm } from './components/about-and-travel-plans-registartion-form';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { eventsApi, profilesApi } from '@/api';
import { useTranslation } from 'react-i18next';
import { EmailForm } from '../email-form';
import styles from './form-registration.module.scss'
import { useSuccessfulMessage } from '@/store/useSuccessfulMessage';
import WebApp from '@twa-dev/sdk';
import { DefaultEmailHeader } from '../default-email-header';
import { RegistrationEmailFormHeader } from '../registration-email-form-header';
import { requestOTPForProfile, verifyOTPForProfile } from '@/utils/email-profile-actions';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
    step?: string;
    isLoadingOncePage?: boolean;
    onChangeIsLoadingPage?: (bol: boolean) => void;
}

export const FormRegistration: React.FC<Props> = ({ step, isLoadingOncePage, onChangeIsLoadingPage }) => {
    const userData = useAuth((state) => state.getUser());
    const { mutateAsync: updateUserData } = useUpdateUserData();
    const handleUpdateUserData = async (data: any) => {
        if (!userData) return;
        await updateUserData(data);
    };

    const navigateToNextStep = useNavigate();
    const nextStep = useCallback(
        (step: string) =>
            navigateToNextStep(
                generatePath(
                    routesApp.registrationV2,
                    { stepNumer: step }
                )
            ),
        [navigateToNextStep],
    );
    
    useEffect(() => {
        if (userData && isLoadingOncePage) {
            onChangeIsLoadingPage?.(false);
            // Если пользователь уже отмечен как завершенный - не душим его повторным проходом по регистрации
            // кидаем сразу в ленту
            if (userData.state === 'complete') {
                navigateToNextStep(generatePath(routesApp.browse));
                return
            }

            // Проверяем, на каком шаге нужно продолжить регистрацию
            if (!userData?.public_name) {
                nextStep('1');
                return;
            }
            if (!userData?.dob) {
                nextStep('2');
                return;
            }
            if (!userData?.photos.length) {
                nextStep('3');
                return;
            }
            if (!userData?.about_me || !userData?.travel_plans) {
                nextStep('4');
                return;
            }
            if (!userData?.gender || !userData?.looks_for) {
                nextStep('5');
                return;
            }
            if (!userData?.purpose) {
                nextStep('6');
                return;
            }
            if (!userData?.regions?.length) {
                nextStep('7');
                return;
            }
            nextStep('7');
            // force skip optional email step
        }
    }, []);

    const { t } = useTranslation();

    const completeRegistration = useAuth(state => state.completeRegistration)

    const { setIsOpen } = useSuccessfulMessage(state => state)

    const queryClient = useQueryClient()

    const completeForm = async () => {
        await completeRegistration()
        await queryClient.invalidateQueries({ queryKey: ['profile'] })
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                callYaMetrika('complete_registration2')
                eventsApi.updateUserEvent('complete_registration2')
                navigateToNextStep(routesApp.browse)
                eventsApi.updateUserEvent('registration_step_complete_search_direction_screen')
                setIsOpen(true)
                resolve()
            }, 500)
        })
    }

    const goToEmailStep = async () => nextStep('8')

    switch (step) {
        case '1':
            return (
                <NameRegistartionForm
                    userData={userData}
                    onUpdateUserData={handleUpdateUserData}
                    nextStep={() => nextStep('2')} />
            );
        case '2':
            return (
                <DateRegistartionForm
                    userData={userData}
                    onUpdateUserData={handleUpdateUserData}
                    nextStep={() => nextStep('3')} />
            );
        case '3':
            return (
                <UploadPhotoRegistartionForm userData={userData} />
            );
        case '4':
            return (
                <AboutAndTravelPlansRegistartionForm
                    userData={userData}
                    onUpdateUserData={handleUpdateUserData}
                    nextStep={() => nextStep('5')} />
            );
        case '5':
            return (
                <SettingInfoRegistrationForm
                    userData={userData}
                    onUpdateUserData={handleUpdateUserData}
                    nextStep={() => nextStep('6')} />
            );
        case '6':
            return (
                <TargetInfoRegistrationForm
                    userData={userData}
                    onUpdateUserData={handleUpdateUserData}
                    nextStep={() => nextStep('7')} />
            );
        case '7':
            return (
                <>
                    <DirectionInfoRegistrationForm
                        userData={userData}
                        onUpdateUserData={handleUpdateUserData}
                        nextStep={WebApp.initData.length ? goToEmailStep : completeForm}
                    />
                </>
            );
        case '8':
            return (
                <div className={styles.emailFormWrapper}>
                    <EmailForm
                        emailHeader={<RegistrationEmailFormHeader />}
                        useGradient={false}
                        emailFormClass={styles.emailForm}
                        nextBtnStyles={styles.nextBtn}
                        showSkipBtn={true}
                        skipBtnStyles={styles.skipBtn}
                        skipBtnTextColor={'black'}
                        requestOTP={requestOTPForProfile}
                        verifyOTP={verifyOTPForProfile}
                        onLogin={completeForm}
                        onSkip={completeForm} />
                </div>
            )
        default:
            return <>{t('registration.error404')}</>;
    }
};
